import { faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import RunStatusBadge, { type StatusTone } from './RunStatusBadge.tsx';
import {
    formatNextRun,
    formatRunTimestamp,
    fromAutomationState,
    RUNS_PAGE_SIZE,
    runErrorMessage,
} from './scheduleFormat.ts';
import FilterDropdown from '../../../core/components/Filter.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { Line } from '../../../core/components/Loader.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import TablePagination from '../../../core/components/TablePagination.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import { CONVERSATION_PATH } from '../../constants.ts';

const ChevronIcon = withIcon(faChevronRight);

export type RunStatusFilter = 'succeeded' | 'failed' | null;

const LOADING_ROWS = 3;

const RUN_TONE: Record<string, StatusTone> = { succeeded: 'success', failed: 'danger' };

const statusLabel = (t, status: string) => {
    if (status === 'failed') return t('automations.detail.failed');
    if (status === 'succeeded') return t('automations.detail.succeeded');
    return t('automations.detail.running');
};

const runDetails = (t, run: any) => {
    const error = run.status === 'failed' ? runErrorMessage(t, run.errorCode || 'RUN_FAILED') : '';
    if (error) return run.isTest ? t('automations.detail.testRunError', { error }) : error;
    return run.isTest ? t('automations.detail.testRun') : '';
};

const Details = ({ text }: { text: string }) =>
    text ? (
        <Tooltip content={text} overflowOnly long>
            <span className="block truncate">{text}</span>
        </Tooltip>
    ) : null;

const RunRow = ({ run, schedule }: { run: any; schedule: { id: string; name: string; conversationId: string } }) => {
    const { t } = useTranslation('chat');
    const navigate = useNavigate();
    // hidden runs (the chat wasn't a destination) have no message to open
    const openRun = run.messageId
        ? () =>
              navigate(url(CONVERSATION_PATH, { conversationId: schedule.conversationId }), {
                  state: fromAutomationState(schedule),
              })
        : null;
    const cellClassName = openRun ? 'transition-colors group-hover:bg-surface-medium' : '';
    return (
        <Table.Row className={openRun ? 'cursor-pointer' : undefined} onClick={openRun ?? undefined}>
            <Table.Cell className={cellClassName}>
                <RunStatusBadge tone={RUN_TONE[run.status] ?? 'agent'}>{statusLabel(t, run.status)}</RunStatusBadge>
            </Table.Cell>
            <Table.Cell className={classnames('whitespace-nowrap', cellClassName)}>
                {formatRunTimestamp(run.createdAt)}
            </Table.Cell>
            <Table.Cell className={cellClassName}>
                <Details text={runDetails(t, run)} />
            </Table.Cell>
            <Table.Cell className={classnames('pl-0', cellClassName)}>
                {openRun && (
                    <div className="flex justify-end">
                        <Tooltip content={t('automations.detail.viewRun')}>
                            <RowControlIconButton
                                type="button"
                                aria-label={t('automations.detail.viewRun')}
                                className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-brand-strong"
                                onClick={(event) => {
                                    // the row handles the same click
                                    event.stopPropagation();
                                    openRun();
                                }}
                            >
                                <ChevronIcon className="text-content-medium" />
                            </RowControlIconButton>
                        </Tooltip>
                    </div>
                )}
            </Table.Cell>
        </Table.Row>
    );
};

/** The run in progress, ahead of the finished ones: runs only list a run once it has ended. */
const RunningRow = ({ startedAt }: { startedAt?: string | null }) => {
    const { t } = useTranslation('chat');
    return (
        <Table.Row>
            <Table.Cell>
                <RunStatusBadge tone="agent">{t('automations.detail.running')}</RunStatusBadge>
            </Table.Cell>
            <Table.Cell className="whitespace-nowrap">
                {formatRunTimestamp(startedAt ?? new Date().toISOString())}
            </Table.Cell>
            <Table.Cell>
                <Details text={t('automations.detail.working')} />
            </Table.Cell>
            <Table.Cell />
        </Table.Row>
    );
};

const RunsTable = ({ children }: { children: React.ReactNode }) => {
    const { t } = useTranslation('chat');
    return (
        <Table scrollable className="w-full min-w-2xl">
            <Table.Head>
                <Table.Row>
                    <Table.HeadCell className="w-40">{t('automations.detail.columns.status')}</Table.HeadCell>
                    <Table.HeadCell className="w-56">{t('automations.detail.columns.run')}</Table.HeadCell>
                    <Table.HeadCell>{t('automations.detail.columns.details')}</Table.HeadCell>
                    <Table.HeadCell className="w-14" />
                </Table.Row>
            </Table.Head>
            <Table.Body>{children}</Table.Body>
        </Table>
    );
};

interface ScheduleRunsProps {
    /** the schedule with one page of `runs` and their `runsCount` for the current filter */
    schedule: any;
    /** the page for the current filter / page isn't there yet */
    isLoading: boolean;
    status: RunStatusFilter;
    onStatusChange: (status: RunStatusFilter) => void;
    page: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
}

/** The schedule's run history: a status filter, one page of runs (the run in progress first) and the pagination. */
const ScheduleRuns = ({
    schedule,
    isLoading,
    status,
    onStatusChange,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
}: ScheduleRunsProps) => {
    const { t, i18n } = useTranslation('chat');
    const runs = schedule.runs || [];
    const runsCount: number = schedule.runsCount ?? 0;
    const showRunning = schedule.lastRunStatus === 'running' && !status && page === 1;

    const emptyText = status
        ? t(status === 'failed' ? 'automations.detail.noFailedRuns' : 'automations.detail.noSucceededRuns')
        : schedule.active && schedule.nextRunAt
          ? t('automations.detail.noRuns', {
                when: formatNextRun(schedule.nextRunAt, i18n.language, schedule.timezone),
            })
          : t('automations.detail.noRunsYet');

    let body: React.ReactNode;
    if (isLoading)
        body = (
            <RunsTable>
                {Array.from({ length: LOADING_ROWS }, (_, row) => (
                    <Table.Row key={row}>
                        {Array.from({ length: 4 }, (_, cell) => (
                            <Table.Cell key={cell}>
                                {cell < 3 && (
                                    <div className="max-w-96 opacity-10">
                                        <Line />
                                    </div>
                                )}
                            </Table.Cell>
                        ))}
                    </Table.Row>
                ))}
            </RunsTable>
        );
    else if (runs.length === 0 && !showRunning)
        // never an empty table: one line of text
        body = <p className="m-0 text-sm text-content-medium">{emptyText}</p>;
    else
        body = (
            <RunsTable>
                {showRunning && <RunningRow startedAt={schedule.lastRunStartedAt} />}
                {runs.map((run) => (
                    <RunRow key={run.id} run={run} schedule={schedule} />
                ))}
            </RunsTable>
        );

    return (
        <section className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
                <h2 className="m-0 text-sm font-medium text-content-strong">{t('automations.detail.runs')}</h2>
                {(status || runsCount > 0) && (
                    <FilterDropdown
                        label={t('automations.detail.statusFilter')}
                        options={[
                            { label: t('automations.detail.allRuns'), value: 'all' },
                            { label: statusLabel(t, 'succeeded'), value: 'succeeded' },
                            { label: statusLabel(t, 'failed'), value: 'failed' },
                        ]}
                        selected={status ? [status] : []}
                        onSelectedChange={(selected) =>
                            onStatusChange(selected[0] === 'succeeded' || selected[0] === 'failed' ? selected[0] : null)
                        }
                        formatLabel={({ defaultLabel }) => {
                            if (!status) return defaultLabel;
                            const value = statusLabel(t, status);
                            return isLoading
                                ? t('automations.detail.statusFilterValue', { status: value })
                                : t('automations.detail.statusFilterActive', { status: value, count: runsCount });
                        }}
                    />
                )}
            </div>
            {body}
            {!isLoading && runsCount > RUNS_PAGE_SIZE && (
                <TablePagination
                    totalCount={runsCount}
                    activePage={page}
                    itemsPerPage={pageSize}
                    setActivePage={onPageChange}
                    setItemsPerPage={onPageSizeChange}
                />
            )}
        </section>
    );
};

export default ScheduleRuns;
